/**
 * `PendingEscalationsProvider` — a single, site-scoped source of truth
 * for the count of PENDING escalations (held mandates awaiting a human).
 *
 * Why a shared store (founder feedback 2026-07-02): the nav badge used
 * to run its OWN 60s poller (`use-pending-escalations.ts`), disconnected
 * from the Resolution screen. Two symptoms fell out of that split:
 *   - new escalations took up to 60s to surface on the badge; and
 *   - resolving one didn't decrement the badge until a full page refresh
 *     (the Resolution view only mutated its own local list).
 *
 * This provider fixes both by owning the count centrally:
 *   - it polls on a 20s cadence (paused on hidden tabs, refetched the
 *     moment a tab becomes visible again); and
 *   - the Resolution view calls `markResolved()` on a successful
 *     approve/reject, which decrements the badge OPTIMISTICALLY (instant)
 *     while the next poll reconciles against the server.
 *
 * The count is scoped to the active site and resets to 0 across a site
 * switch, so a per-site figure never leaks into another site's badge.
 * `forbidden` / error / provider-less all resolve to 0 (badge hides),
 * matching the prior hook's fail-quiet behavior.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { listEscalations } from '../api/escalations.js';
import { useOptionalHttp } from '../api/http-context.js';
import { useSites } from './providers.js';

/** Poll cadence — down from the old badge's 60s so a NEW escalation
 *  surfaces within ~20s without a refresh. Paused on hidden tabs. */
const POLL_MS = 20_000;

interface PendingEscalationsCtxValue {
  /** Live count of pending escalations for the active site (0 when
   *  loading / forbidden / no site / no provider). */
  count: number;
  /** Optimistic decrement — call after a successful resolve so the badge
   *  drops immediately; the next poll reconciles with the server. */
  markResolved: () => void;
  /** Force an immediate re-fetch (e.g. after a mutation the caller wants
   *  reflected sooner than the poll interval). */
  refresh: () => void;
}

const PendingEscalationsCtx = createContext<PendingEscalationsCtxValue | null>(null);

/** Stable fail-quiet stub for consumers mounted outside the provider
 *  (isolated component tests). Frozen + module-level so its mutator
 *  identities never churn a consumer's memoized callbacks. */
const STUB: PendingEscalationsCtxValue = Object.freeze({
  count: 0,
  markResolved: () => {},
  refresh: () => {},
});

export function PendingEscalationsProvider({ children }: { children: ReactNode }): JSX.Element {
  const http = useOptionalHttp();
  const { activeSite } = useSites();
  const site_id = activeSite?.site_id;
  const [count, setCount] = useState(0);

  // Generation guard — a site switch or a newer fetch invalidates any
  // in-flight response so a slow reply can't clobber a fresher count.
  const genRef = useRef(0);

  const refresh = useCallback((): void => {
    if (http === null || site_id === undefined || site_id.length === 0) {
      setCount(0);
      return;
    }
    const gen = ++genRef.current;
    void listEscalations(http, 'pending', site_id).then((result) => {
      if (gen !== genRef.current) return; // stale — a newer fetch superseded us
      setCount(result.kind === 'ok' ? result.items.length : 0);
    });
  }, [http, site_id]);

  // Reset + fetch on site change; poll on a visible-tab cadence; refetch
  // immediately when a hidden tab becomes visible again (so a return to
  // the tab shows a current badge without waiting out the interval).
  useEffect(() => {
    // Bump the generation so any in-flight fetch for the previous site is
    // discarded, and clear the stale count while the new one loads.
    genRef.current += 1;
    setCount(0);
    if (http === null || site_id === undefined || site_id.length === 0) {
      return undefined;
    }
    refresh();
    const handle = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      refresh();
    }, POLL_MS);
    const onVisible = (): void => {
      if (typeof document !== 'undefined' && !document.hidden) refresh();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }
    return () => {
      // Discard any in-flight fetch so a late resolve can't setCount after
      // unmount (React would warn, and it leaks across a test teardown).
      genRef.current += 1;
      clearInterval(handle);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [http, site_id, refresh]);

  const markResolved = useCallback((): void => {
    // Optimistic — instant feedback on the badge. The poll reconciles the
    // authoritative figure (and re-adds if a concurrent escalation landed).
    setCount((c) => Math.max(0, c - 1));
  }, []);

  // Memoized so consumers re-render only when the count actually changes,
  // not on every incidental re-render of the provider's parents.
  const value = useMemo<PendingEscalationsCtxValue>(
    () => ({ count, markResolved, refresh }),
    [count, markResolved, refresh],
  );

  return (
    <PendingEscalationsCtx.Provider value={value}>
      {children}
    </PendingEscalationsCtx.Provider>
  );
}

/**
 * Read the shared pending-escalations store. Returns a fail-quiet stub
 * (count 0, no-op mutators) when called outside the provider — e.g. in
 * isolated component tests that don't mount the full provider tree — so
 * the badge and Resolution view render without throwing.
 */
export function usePendingEscalationsStore(): PendingEscalationsCtxValue {
  const ctx = useContext(PendingEscalationsCtx);
  return ctx ?? STUB;
}
